import React from 'react';

import {
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
} from 'lucide-react'; // Import Lucide React icons

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize: number;
  onPageSizeChange: (size: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
}) => {
  const pageSizes = [10, 20, 50];

  // Calculate the start and end page numbers to display
  const maxPagesToShow = 5;
  let startPage = Math.max(1, currentPage - 2);
  let endPage = Math.min(totalPages, currentPage + 2);

  if (endPage - startPage < maxPagesToShow - 1) {
    if (currentPage < Math.ceil(maxPagesToShow / 2)) {
      endPage = Math.min(totalPages, maxPagesToShow);
    } else if (currentPage > totalPages - Math.floor(maxPagesToShow / 2)) {
      startPage = Math.max(1, totalPages - maxPagesToShow + 1);
    }
  }

  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex justify-between items-center py-2">
      {/* Page Size Selector */}
      <div>
        <label className="mr-2 text-sm text-ink-muted dark:text-white/60">
          Page Size:
        </label>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-ink transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 dark:border-white/20 dark:bg-white/5 dark:text-white"
        >
          {pageSizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center text-sm">
        <button
          type="button"
          className={`mr-1 rounded-md px-3 py-1 transition-colors ${
            currentPage === 1
              ? 'cursor-not-allowed text-ink-muted/50 dark:text-white/25'
              : 'cursor-pointer text-ink hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10'
          }`}
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          className={`mr-1 rounded-md px-3 py-1 transition-colors ${
            currentPage === 1
              ? 'cursor-not-allowed text-ink-muted/50 dark:text-white/25'
              : 'cursor-pointer text-ink hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10'
          }`}
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        {pages.map((page) => (
          <button
            type="button"
            key={page}
            // Black on gold for the current page (9.05:1). It was white on gold, 2.32:1.
            className={`mx-1 min-w-[2rem] cursor-pointer rounded-md px-3 py-1 font-semibold transition-colors ${
              currentPage === page
                ? 'bg-primary text-secondary'
                : 'bg-gray-100 text-ink hover:bg-gray-200 dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/20'
            }`}
            onClick={() => onPageChange(page)}
          >
            {page}
          </button>
        ))}

        <button
          type="button"
          className={`ml-1 rounded-md px-3 py-1 transition-colors ${
            currentPage === totalPages
              ? 'cursor-not-allowed text-ink-muted/50 dark:text-white/25'
              : 'cursor-pointer text-ink hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10'
          }`}
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
        <button
          type="button"
          className={`ml-1 rounded-md px-3 py-1 transition-colors ${
            currentPage === totalPages
              ? 'cursor-not-allowed text-ink-muted/50 dark:text-white/25'
              : 'cursor-pointer text-ink hover:bg-gray-100 dark:text-white/80 dark:hover:bg-white/10'
          }`}
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
